import { useMatches } from '@tanstack/react-router'
import { mainnet } from 'viem/chains'
import { useChainId } from 'wagmi'

import { getChainConfigEntry } from '@/config/chain'
import { SUPPORTED_CHAIN_IDS } from '@/config/chain/constants'
import { ChainConfigEntry } from '@/config/chain/types'
import { Route } from '@/routing/router'
export interface UsePageChainIdResult {
  chainId: number
  pageSupported: boolean
  pageName: string
  activeRouteGroup: RouteGroup | null
  supportedChainNames: string[]
}

export function usePageChainId(): UsePageChainIdResult {
  const chainId = useChainId()
  const matches = useMatches()
  const supportedRoutes = getSupportedRoutes(getChainConfigEntry(chainId))

  const deepestMatch = matches.at(-1)
  const currentRoute = deepestMatch?.fullPath

  const supportedChainNames = getSupportedChainNamesForRoute(currentRoute)
  const pageName = currentRoute ? (routeNamesMap[currentRoute] ?? '') : ''

  const activeRouteGroup = Object.entries(routeGroups).find(([_, routes]) =>
    currentRoute ? routes.includes(currentRoute) : false,
  )?.[0] as RouteGroup | undefined

  if (!currentRoute || !routeNamesMap[currentRoute]) {
    // @note When the current route is not found in routeNamesMap, the page is
    // not existent *but* supported so we can display 404 page
    return { chainId, pageSupported: true, pageName: '', activeRouteGroup: null, supportedChainNames }
  }

  if (supportedRoutes.includes(currentRoute)) {
    return { chainId, pageSupported: true, pageName, activeRouteGroup: activeRouteGroup ?? null, supportedChainNames }
  }

  return {
    chainId: mainnet.id,
    pageSupported: false,
    pageName,
    activeRouteGroup: activeRouteGroup ?? null,
    supportedChainNames,
  }
}

export const routeGroups = {
  borrow: ['/borrow', '/my-portfolio', '/markets', '/markets/$chainId/$asset'],
  savings: ['/', '/savings/$domain/$savingsToken'],
  sparkRewards: ['/rewards'],
  sparkToken: ['/spk/staking', '/spk/airdrop', '/spk/farm', '/spk/governance'],
  sparkPoints: ['/points/{-$referralCode}'],
  swap: ['/swap'],
} satisfies Record<string, Route[]>

export type RouteGroup = keyof typeof routeGroups

export function getSupportedRoutes(chainConfigEntry: ChainConfigEntry): Route[] {
  const borrowRoutes = chainConfigEntry.sparkLend
    ? chainConfigEntry.sparkLend.easyBorrow
      ? routeGroups.borrow
      : routeGroups.borrow.filter((route) => route !== '/borrow')
    : []

  return [
    ...borrowRoutes,
    ...(chainConfigEntry.savings ? routeGroups.savings : []),
    ...(chainConfigEntry.spk ? routeGroups.sparkToken : []),
    ...(chainConfigEntry.savings?.psmStables ? routeGroups.swap : []),
    ...routeGroups.sparkPoints,
    ...routeGroups.sparkRewards,
  ]
}

export function getSupportedChainNamesForRoute(route: Route | undefined): string[] {
  if (!route) return []
  return SUPPORTED_CHAIN_IDS.filter((chainId) => {
    const chainConfigEntry = getChainConfigEntry(chainId)
    const supportedRoutes = getSupportedRoutes(chainConfigEntry)
    return supportedRoutes.includes(route)
  }).map((chainId) => getChainConfigEntry(chainId).meta.name)
}

const routeNamesMap: Record<Route, string> = {
  '/': 'Savings',
  '/borrow': 'Easy Borrow',
  '/my-portfolio': 'Portfolio',
  '/markets': 'Markets',
  '/markets/$chainId/$asset': 'Market',
  '/savings/$domain/$savingsToken': 'Savings',
  '/rewards': 'Rewards',
  '/spk/staking': 'SPK Staking',
  '/spk/airdrop': 'SPK Airdrop',
  '/spk/farm': 'SPK Farm',
  '/spk/governance': 'SPK Governance',
  '/points/{-$referralCode}': 'Points',
  '/swap': 'Swap',
}
